import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import { createE2EClass } from '@Utils/String';

import './Button.scss';

export default function Button(props) {
  const { children, onClick, disabled, buttonConfig, iconConfig, ...otherProps } = props;

  const iconTypePrefix = iconConfig.iconTypePrefix || 'icon';
  const labelWrapper = buttonConfig.label ? (
    <span
      className={classnames(buttonConfig.labelWrapperClassName, {
        'rx-border-none': buttonConfig.noBorder,
      })}>
      {buttonConfig.label}
    </span>
  ) : null;

  const iconWrapper = iconConfig.name ? (
    <svg
      ref={iconConfig.ref}
      className={classnames(iconConfig.className, 'els-btn-icon', {
        [`els-btn-icon-${iconConfig.size}`]: iconConfig.size,
        'rx-border-none': iconConfig.noBorder,
      })}
      focusable={false}>
      {iconConfig.title && <title>{iconConfig.title}</title>}
      <use xlinkHref={`#${iconTypePrefix}-${iconConfig.name}`} />
    </svg>
  ) : null;

  const iconAndLabel =
    iconConfig.position === 'before' ? (
      <>
        {iconWrapper}
        {labelWrapper}
      </>
    ) : (
      <>
        {labelWrapper}
        {iconWrapper}
      </>
    );

  const buttonClasses = [
    { [`els-btn-${buttonConfig.type}`]: buttonConfig.type },
    { [`els-btn-${buttonConfig.size}`]: buttonConfig.size },
    { [`${buttonConfig.color}`]: buttonConfig.color },
    { 'els-btn-icon-only': buttonConfig.iconOnly },
    { [`${createE2EClass(buttonConfig.e2eName, true)}`]: buttonConfig.e2eName },
    { disabled: buttonConfig.aTag && disabled },
  ];

  const buttonTag = (
    // eslint-disable-next-line react/button-has-type,jsx-a11y/no-redundant-roles
    <button
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={buttonConfig.ariaLabel}
      role="button"
      tabIndex={buttonConfig.tabIndex ? buttonConfig.tabIndex : 0}
      className={classnames(buttonConfig.className, 'els-btn', buttonClasses)}
      onClick={disabled ? undefined : onClick}
      ref={buttonConfig.ref}
      title={buttonConfig.title}
      {...otherProps}>
      {children}

      {iconAndLabel}
    </button>
  );

  const aTag = (
    <a
      href={buttonConfig.href}
      role="button"
      aria-disabled={disabled}
      aria-label={buttonConfig.ariaLabel}
      tabIndex={`${buttonConfig.tabIndex ? buttonConfig.tabIndex : 0}`}
      className={classnames(buttonConfig.className, 'els-btn', buttonClasses)}
      onClick={disabled ? undefined : onClick}
      ref={buttonConfig.ref}
      title={buttonConfig.title}
      target={buttonConfig.target}
      {...otherProps}>
      {children}

      {iconAndLabel}
    </a>
  );

  return buttonConfig.aTag ? aTag : buttonTag;
}

export class ButtonType {
  static LINK = 'link';

  static ICON_LINK = 'icon-link';

  static ICON_ONLY = 'icon-only';

  static PRIMARY = 'primary';

  static SECONDARY = 'secondary';

  static TERTIARY = 'tertiary';

  static PRIMARY_ALT = 'primary-alt';

  static SECONDARY_ALT = 'secondary-alt';

  static TERTIARY_ALT = 'tertiary-alt';

  static QUATERNARY = 'quaternary';
}

const { ICON_LINK, ICON_ONLY, PRIMARY, SECONDARY, TERTIARY, QUATERNARY, PRIMARY_ALT, SECONDARY_ALT, TERTIARY_ALT, LINK } = ButtonType;

Button.defaultProps = {
  onClick: noop,
  buttonConfig: {
    aTag: false,
    ref: {},
    title: '',
    ariaLabel: '',
  },
  iconConfig: {
    position: 'after',
    ref: {},
  },
};

Button.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  buttonConfig: PropTypes.shape({
    type: PropTypes.oneOf([ICON_LINK, ICON_ONLY, PRIMARY, QUATERNARY, SECONDARY, TERTIARY, PRIMARY_ALT, SECONDARY_ALT, TERTIARY_ALT, LINK]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    className: PropTypes.string,
    size: PropTypes.string,
    e2eName: PropTypes.string.isRequired,
    tabIndex: PropTypes.number,
    color: PropTypes.string,
    aTag: PropTypes.bool,
    href: PropTypes.string,
    ref: PropTypes.object,
    noBorder: PropTypes.bool,
    title: PropTypes.string,
    ariaLabel: PropTypes.string,
    iconOnly: PropTypes.bool,
  }),
  iconConfig: PropTypes.shape({
    iconTypePrefix: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.string,
    position: PropTypes.string,
    className: PropTypes.string,
    ref: PropTypes.object,
  }),
};
